import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { cellValueFormattingRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import { Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { FC } from 'react';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { maxNumberOfTransactionsInYear } from './utils/transactionUtils';

type ValueSelector<T> = (item: T) => number;

interface ValuationMovementProps {
  instrument: InstrumentDefinitionWithId;
  transactionsByCaseAndYear: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >;
  transactionType: TransactionType;
  headerLabel?: string;
  rowLabel: string;
  valueSelector: ValueSelector<Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto>;
  cellValueFormattingRules: cellValueFormattingRules;
}

export const ValuationMovement: FC<ValuationMovementProps> = ({
  instrument,
  transactionsByCaseAndYear,
  transactionType,
  rowLabel,
  valueSelector,
  cellValueFormattingRules,
}) => {
  const maxNumberOfTransactions = maxNumberOfTransactionsInYear(transactionsByCaseAndYear);

  if (!maxNumberOfTransactions) {
    return null;
  }

  const displayMultipleRows = maxNumberOfTransactions > 1;
  const transactionRowIndexes = Array.from({ length: maxNumberOfTransactions }, (_, i) => i);

  return (
    <>
      {transactionRowIndexes.map((index) => {
        const rowLabelWithIndex = rowLabel + (displayMultipleRows ? ` ${index + 1}` : '');

        return (
          <TableSectionValuesPerForecast
            key={`${instrument.instrumentId}-${transactionType}-${index}`}
            title={rowLabelWithIndex}
            renderSpacer={false}
            renderCell={({ forecast, caseItem }) => {
              const transaction =
                transactionsByCaseAndYear[caseItem.caseId][forecast.forecastYear]?.[index];
              if (!transaction) {
                return null;
              }
              return (
                <CellValue
                  {...cellValueFormattingRules}
                  value={valueSelector(transaction)}
                  dataTestId={`${transactionType}-cell-${caseItem.caseId}-${forecast.forecastId}-${index}`}
                />
              );
            }}
          />
        );
      })}
    </>
  );
};
